import { PagedResponse } from '@elotech/components';
import { Entidade, FilterUtils, RoleUsuarioDTO } from '@elotech/components';
import axios, { AxiosPromise } from 'axios';

import { UsuarioLogin, UsuarioNivel, UsuarioProvider } from '../type';
import ServiceUtils from '../utils/ServiceUtils';
import KeycloakService from './KeycloakService';

const SINGLE_REALM = 'OxyElotech';

const searchUserByDocument = (tipoDocumento: 'JURIDICA' | 'FISICA') => (
  documento: string
): AxiosPromise<any> =>
  axios.get(`/api/usuarios?cpf-cnpj=${documento}&tipo-pessoa=${tipoDocumento}`);

export const getUserData = (): AxiosPromise<any> =>
  axios.get(`/gateway/usuarios/me`);

export const getUserDataDirectly = (): AxiosPromise<any> =>
  axios.get(`/api/usuarios/me`);

export const patchUser = (userData: object): AxiosPromise<any> =>
  axios.patch(`/api/usuarios/me`, userData);

export const userExists = (): AxiosPromise<any> =>
  axios.get(`/gateway/public/user-exists`);

export const addUser = (userData: object): AxiosPromise<any> =>
  axios.post(`/api/usuarios/add-user`, userData);

export const searchUserByCpf = searchUserByDocument('FISICA');

export const searchUserByCnpj = searchUserByDocument('JURIDICA');

export const resetPassword = (): AxiosPromise<any> =>
  axios.get('/api/usuarios/reset-password');

export const changePassword = (
  userId: string,
  passwordObject: {
    password: string;
    temporaryPassword: boolean;
  }
): AxiosPromise<void> =>
  axios.put(
    `${KeycloakService.getUrl()}/usuario/${userId}/reset-password`,
    passwordObject,
    {
      headers: {
        Authorization: 'Bearer ' + KeycloakService.getToken()
      }
    }
  );

export const searchUsuarioByNome = (
  searchParam: string = '',
  page: object
): AxiosPromise<any> =>
  axios.get(`/api/usuarios/search?search=${searchParam}`, {
    params: page
  });

export const searchUsuarioOnSameEntidade = (
  searchParam: string = '',
  role: string = '',
  page: object
): AxiosPromise<any> =>
  axios.get(`/api/usuarios/grupo?search=${searchParam}&role=${role}`, {
    params: page
  });

export const adicionarUsuarioAutorizado = (
  cpfCnpj: string
): AxiosPromise<any> =>
  axios.post(`/api/usuarios/me/usuario-autorizado/${cpfCnpj}`);

export const getUsuarioAutorizadoById = (): AxiosPromise<any> =>
  axios.get('/api/usuarios/me/usuario-autorizado');

export const removerUsuarioAutorizado = (cpfCnpj: string): AxiosPromise<any> =>
  axios.delete(`/api/usuarios/me/usuario-autorizado/${cpfCnpj}`);

export const getUsuarios = (searchParams: string, page: object) =>
  axios.get(`/api/usuarios?${ServiceUtils.buildSearchParams(searchParams)}`, {
    params: { defaultSort: { sort: 'nome' }, ...page }
  });

export const autoComplete = (searchParams: string) =>
  axios.get(`/api/usuarios`, {
    params: {
      defaultSort: {
        sort: 'nome',
        search: FilterUtils.buildFilter(['cpfCnpj', 'nome'])(searchParams)
      }
    }
  });

export const getMatriculas = (cpf: string): AxiosPromise<PagedResponse<any>> =>
  axios.get(`/api/usuarios/${cpf}/matriculas`);

export const getUsuarioByCpfCnpj = (cpfCnpj: string) =>
  axios.get(`/api/usuarios/cpf-cnpj/${cpfCnpj}`);

export const getUsuarioAcessoCidadao = (): AxiosPromise<any> =>
  axios.get(`/api/usuarios/acesso-cidadao`);

export const getUsuarioTributosByCpf = (cpfCnpj: string) =>
  axios.get(`/api/usuarios/tributos/${cpfCnpj}`);

export const getUserPermissions = (
  entidade: Entidade | undefined
): AxiosPromise<RoleUsuarioDTO> => {
  return axios.get('/api/usuarios/permissoes', {
    params: {
      entidade: entidade?.id
    }
  });
};

export const findNivel = (id: string): AxiosPromise<UsuarioNivel> =>
  axios.get(`/painel-api/api/usuarios-externos/${id}/nivel`);

export const findIdentityProviders = (
  userId: string
): AxiosPromise<UsuarioProvider> => {
  const isMultiRealm = KeycloakService.getInstance().realm !== SINGLE_REALM;

  return axios.get(
    `/painel-api/api/usuario/${userId}/providers?isExterno=${isMultiRealm}`
  );
};

export const getActiveSessions = () =>
  axios.get('/painel-api/api/usuario/me/session');

export const encerrarTodasSessoes = () =>
  axios.delete(`${KeycloakService.getUrl()}/usuario/me/session`, {
    headers: {
      Authorization: 'Bearer ' + KeycloakService.getToken()
    }
  });

export const encerrarSessaoPorId = (sessaoId: string) =>
  axios.delete(`${KeycloakService.getUrl()}/usuario/me/session/${sessaoId}`, {
    headers: {
      Authorization: 'Bearer ' + KeycloakService.getToken()
    }
  });

export const removeProviderLinkFromUser = (
  userId: string,
  provider: string
): AxiosPromise<void> =>
  axios.delete(
    `${KeycloakService.getUrl()}/usuario/providers/${userId}/${provider}`,
    {
      headers: {
        Authorization: 'Bearer ' + KeycloakService.getToken()
      }
    }
  );

export const getCurrentUserLastLogins = (): AxiosPromise<UsuarioLogin[]> =>
  axios.get(`${KeycloakService.getUrl()}/usuario/me/login`, {
    headers: {
      Authorization: 'Bearer ' + KeycloakService.getToken()
    }
  });
